/**
 * The lesson's second screen ("stage"), 2026-09-27; a real page since Lesson
 * Dashboard pass 3 release 5 (2026-09-28).
 *
 * WHAT ANTHONY ASKED FOR: "position a window and leave it there so no matter
 * who is working can just press a button and it works", then, settling the
 * hardware: "same speakers as the mains. its an extended monitor. we just need
 * a window that can be dragged to the second screen". So each room's spare
 * monitor is an EXTENDED DISPLAY of the room's Mac, and the stage is a
 * browser window on that Mac, dragged there once and left. Every lesson
 * taught ON THAT MAC, by any teacher signed in in that browser, drives it.
 *
 * HOW. The stage is the `/stage` route (stage/StagePage.js): a real page that
 * can be bookmarked, reopened after a restart, put in full screen, and that
 * survives the lesson tab reloading or moving between lessons. The dashboard
 * and the stage are the same origin in the same browser, which is exactly
 * what a BroadcastChannel connects: no database relay, no public token, no
 * new collection or rules. (Until this release the window held a document
 * the dashboard `document.write`-d into it, which died with the tab that
 * wrote it and could not be reopened on its own.)
 *
 * The channel carries:
 *   dashboard -> stage   lyrics / clock (useStageLyrics), video, mode, reset, close, ping
 *   stage -> dashboard   hello (on open), stage-here (heartbeat + reply to
 *                        ping, with its mode), stage-bye (on close)
 * Presence is the stage's heartbeat, so the dashboard can say "On · Stage
 * window · Lyrics" or offer to open one. When a stage is present, showing a
 * video never opens anything; it just sends.
 *
 * OUT OF SCOPE, stated so nobody assumes it: a lesson taught from an iPad does
 * not reach the Mac's stage (a different browser, so a different channel).
 * The Second screen control says so there. A room doc in Firestore the stage
 * subscribes to would layer on top of this page without changing it.
 *
 * SECURITY: nothing here or on the stage interpolates user data into HTML.
 * Titles, video ids and lyric lines travel as data; the stage renders them
 * through React, and a video id is validated on both sides.
 */

export const STAGE_WINDOW_NAME = 'cyvLessonVideo'; // the same window teachers already park
export const STAGE_CHANNEL = 'cyv-lesson-stage';
export const STAGE_PATH = '/stage';
const FEATURES = 'width=1600,height=900,menubar=no,toolbar=no,location=no,status=no';

// The highlight moves this far AHEAD of the measured time: Whisper's line
// times ran a median 0.43 s late against lrclib's hand sync (2026-09-27), and
// a reader wants the line a beat before it is sung.
export const LEAD_SECONDS = 0.3;

/** A stage that has not been heard from in this long is treated as closed. */
export const PRESENCE_TIMEOUT_MS = 6000;
/** How often the stage says it is there. */
export const HEARTBEAT_MS = 2000;
/** A video asked for while the stage was still opening waits this long. */
const PENDING_MS = 30000;

let channel = null;
let presence = { at: 0, mode: 'idle', has: { lyrics: false, video: false } };
let pending = null; // { msg, until }
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(presence));

const getChannel = () => {
	if (channel) return channel;
	if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') return null;
	channel = new window.BroadcastChannel(STAGE_CHANNEL);
	// One module-level listener keeps presence and hands over a pending video
	// the moment a newly opened stage says hello.
	channel.addEventListener('message', (e) => {
		const m = (e && e.data) || {};
		if (m.type === 'stage-here' || m.type === 'hello') {
			presence = { at: Date.now(), mode: m.mode || presence.mode || 'idle', has: m.has || presence.has };
			notify();
			if (pending && Date.now() < pending.until) {
				const { msg } = pending;
				pending = null;
				channel.postMessage(msg);
			}
		} else if (m.type === 'stage-bye') {
			presence = { at: 0, mode: 'idle', has: { lyrics: false, video: false } };
			notify();
		}
	});
	return channel;
};

/** Send to the stage. Harmless with no stage open (nobody listens). */
export const postStage = (msg) => {
	const ch = getChannel();
	if (ch) ch.postMessage(msg);
};

/** Listen for the stage (its "hello" when it opens mid-song). */
export const onStageMessage = (fn) => {
	const ch = getChannel();
	if (!ch) return () => {};
	const handler = (e) => fn(e.data || {});
	ch.addEventListener('message', handler);
	return () => ch.removeEventListener('message', handler);
};

/** Is a stage window open in this browser right now? */
export const isStagePresent = (nowMs = Date.now()) => presence.at > 0 && nowMs - presence.at < PRESENCE_TIMEOUT_MS;

/** { at, mode, has } of the last word from the stage; `fn` is called on change. */
export const subscribeStagePresence = (fn) => {
	getChannel();
	listeners.add(fn);
	return () => listeners.delete(fn);
};
export const getStagePresence = () => presence;

/** Ask any open stage to say it is there. */
export const pingStage = () => postStage({ type: 'ping' });

/** Open (or bring forward) the stage window at /stage. false = blocked. */
export const openStageWindow = () => {
	if (typeof window === 'undefined') return false;
	getChannel();
	const win = window.open(STAGE_PATH, STAGE_WINDOW_NAME, FEATURES);
	if (!win) return false;
	if (typeof win.focus === 'function') win.focus();
	return true;
};

/**
 * Show a saved video on the stage. With a stage present it only sends; with
 * none it opens /stage and hands the video over when the page says hello.
 * false = the window was blocked.
 */
export const openStageVideo = (videoId, title) => {
	const msg = { type: 'video', videoId: String(videoId || ''), title: String(title || 'Video') };
	getChannel();
	if (isStagePresent()) {
		postStage(msg);
		return true;
	}
	pending = { msg, until: Date.now() + PENDING_MS };
	return openStageWindow();
};

/** Lyrics / Video / Blank from the dashboard (G3). */
export const setStageMode = (mode) => postStage({ type: 'mode', mode });

/**
 * The lesson is over (Anthony, 2026-10-02: "when a lesson is closed, the stage
 * window should go back to rendering the company logo and wait for input from
 * the new lesson"). The stage drops the last lesson's words and video and
 * shows the logo, so the next student never walks in to the previous one's
 * song. Sent when the dashboard leaves a lesson: a route change (Complete,
 * Student didn't come, Back to the portal), a switch to another lesson, or
 * the tab closing.
 */
export const resetStage = () => postStage({ type: 'reset' });

/** Close the stage window from the dashboard (G3). */
export const closeStage = () => postStage({ type: 'close' });

/** For tests: forget presence, pending and the channel. */
export const __resetStageForTests = () => {
	if (channel && typeof channel.close === 'function') channel.close();
	channel = null;
	presence = { at: 0, mode: 'idle', has: { lyrics: false, video: false } };
	pending = null;
	listeners.clear();
};
